import {
  Component,
  EventEmitter,
  Output,
  Signal,
  signal,
  WritableSignal,
  computed,
} from '@angular/core';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { INodeInspectorAdapter } from '@shared/components/joint-js/services/INodeInspectorAdapter';
import { InspectorMode } from '@shared/components/joint-js/components/inspectors/node-inspector/node-inspector.component';
import { NodeValidationError } from '@modules/campaigns/Domain/Interfaces/NodeValidationError';

@Component({
  template: '',
  standalone: true,
})
export abstract class InspectorBase<TNode extends NodeClass> implements INodeInspectorAdapter {
  @Output() closeSidebar = new EventEmitter<void>();

  node: WritableSignal<NodeClass | null> = signal<NodeClass | null>(null);
  mode = signal<InspectorMode>('edit');
  activeTab = signal<'preview' | 'config'>('preview');
  errors = signal<NodeValidationError[]>([]);

  protected changeTimeout?: ReturnType<typeof setTimeout>;

  abstract get nodeValue(): TNode | null;

  readonly visibleTabs = computed(() => {
    const allTabs = this.getTabs();
    return this.mode() === 'edit' ? allTabs : allTabs.filter((t) => t.value === 'preview');
  });

  setNode(nodeSignal: Signal<NodeClass | null>) {
    this.node.set(nodeSignal());
    this.refreshFromConfig();
  }

  setMode(mode: InspectorMode) {
    this.mode.set(mode);
    this.activeTab.set(mode === 'edit' ? 'config' : 'preview');
  }

  setActiveTab(tab: 'preview' | 'config') {
    this.activeTab.set(tab);
  }

  abstract applyChanges(): void;
  abstract getTabs(): { label: string; value: 'preview' | 'config' }[];
  protected abstract refreshFromConfig(): void;

  protected scheduleApplyChanges(delay = 500) {
    if (this.changeTimeout) clearTimeout(this.changeTimeout);
    this.changeTimeout = setTimeout(() => this.applyChanges(), delay);
  }
}
